/* eslint-disable-next-line camelcase, @typescript-eslint/naming-convention */
import { type GTM, type Search } from '@vakantiesnl/types';

import { basicGTMV2Event } from './basicGTMV2Event';
import { accoEventValueConstructor } from './util/accoEventValueConstructor';
import { type Geo } from './util/useMapGeo';

export interface Entity {
	entity_id: string | number;
	entity_level: string;
}

/**
 * loadedSearchResults()
 * Loaded Search Results Event in GTM
 *
 * @param {number} found - Total results found
 * @param {number} pageNumber - Current page number
 * @param {number} pageSize - Amount of results on pages
 * @param {number} sortedBy - Sorting method used on results
 * @param {string[]} accommodationCodes
 * @param {Record<string, unknown>} filters
 * @param {Entity | null} entity
 * @param {Geo | null} geo
 * @param {number} noOfFilters
 * @param {number | undefined} minPrice
 * @param {number | undefined} maxPrice
 */

export const searchPageView = (
	found: number,
	pageNumber: number,
	pageSize: number,
	sortedBy: string,
	accommodationCodes: string[],
	filters: Record<string, unknown>,
	entity: Entity | null,
	geo?: Geo | null,
	noOfFilters = 0,
	minPrice?: number,
	maxPrice?: number,
): GTM.GTMEventV2 => {
	const accommodation_codes: { [key: number]: string } = {};
	accommodationCodes.forEach((id, key) => {
		accommodation_codes[key] = id;
	});

	const value: Record<string, unknown> = {
		no_of_results: found,
		pagination: pageNumber,
		page_size: pageSize,
		sorted_by: sortedBy,
		accommodation_codes,
		no_of_filters: noOfFilters,
		filters,
		geo,
	};

	if (minPrice) {
		value.min_price = minPrice;
	}

	if (maxPrice) {
		value.max_price = maxPrice;
	}

	return basicGTMV2Event({
		type: 'view',
		organism: null,
		molecule: null,
		atom: null,
		action: 'page_view',
		label: null,
		page_type: 'listing',
		sub_page: 'accommodations',
		entity_id: entity?.entity_id?.toString() || null,
		entity_level: entity?.entity_level || null,
		value,
	});
};

export const sortResults = (order: Search.SortingOrder): GTM.GTMEvent => {
	let value;
	switch (order) {
		case 'hotel_review_rating':
			value = 'popularity';
			break;
		case 'price':
			value = 'price asc';
			break;
		case 'price_desc':
			value = 'price desc';
			break;
		default:
			value = 'ratings';
	}
	return {
		event: 'results.sort',
		event_value: {
			value,
			value_type: 'basic',
			source: 'search/filters',
			action: 'sort',
		},
	};
};

export const onSelectCountry = (option: Search.Option): GTM.GTMEvent => ({
	event: 'filter.select',
	event_value: {
		value: 'country',
		value_type: 'filter',
		source: 'search/filters/countries',
		label: 'search',
		action: 'select',
		filter: {
			name: 'country',
			value: option.title,
		},
	},
});

export const onSetPartyComposition = (
	rooms: { adults: number; children: number; babies: number }[],
	entity: Entity | null,
	geo?: Geo | null,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'priority_filters',
		molecule: 'party_picker',
		atom: 'number_picker',
		action: 'select',
		label: `party_composition | ${rooms
			.map((party) => `A${party.adults}C${party.children}B${party.babies}`)
			.join('-')}`,
		page_type: 'listing',
		sub_page: 'overview',
		entity_id: entity?.entity_id?.toString() || null,
		entity_level: entity?.entity_level || null,
		value: {
			geo,
			rooms,
		},
	});

export const onToggleGeoFilter = (
	data: {
		type: 'country' | 'region' | 'city' | 'theme' | 'departureAirport' | 'duration' | 'mealplan';
		id: string;
		added: boolean;
	},
	entity: Entity | null,
): GTM.GTMEventV2 =>
	onFilterSelection(
		data.type,
		`${data.id}_${data.added ? 'added' : 'removed'}`,
		'select',
		'checkbox_group',
		'checkbox',
		entity,
	);

export const onSetDepartureDate = (
	departure_date: string[],
	entity: Entity | null,
	geo?: Geo | null,
): GTM.GTMEventV2 => {
	const [minDate, maxDate] = departure_date;
	const isDateRange = minDate && maxDate;

	return basicGTMV2Event({
		type: 'action',
		organism: 'priority_filters',
		molecule: isDateRange ? 'pick_departure_date_range' : 'pick_departure_date',
		atom: isDateRange ? 'date_range' : 'date',
		action: 'select',
		label: isDateRange ? `date_range | ${departure_date.join('/')}` : `departure_date | ${minDate}`,
		page_type: 'listing',
		sub_page: 'overview',
		entity_id: entity?.entity_id?.toString() || null,
		entity_level: entity?.entity_level || null,
		value: isDateRange
			? {
					date_range: {
						start: minDate,
						end: maxDate,
					},
					geo,
				}
			: { departure_date: minDate, geo },
	});
};

export const onSetDepartureOffset = (
	selected: boolean | number,
	entity: Entity | null,
	geo?: Geo | null,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'priority_filters',
		molecule: 'pick_departure_date',
		atom: 'checkbox',
		action: 'toggle',
		label: `departure_date_offset | ${selected.toString()}`,
		page_type: 'listing',
		sub_page: 'overview',
		entity_id: entity?.entity_id?.toString() || null,
		entity_level: entity?.entity_level || null,
		value: {
			selected,
			geo,
		},
	});

export const onFilterSelection = (
	filterGroup: string,
	value: string,
	action: 'select' | 'deselect',
	molecule: string,
	atom: string,
	entity?: Entity | null,
	geo?: Geo | null,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'filter_list',
		molecule,
		atom,
		action,
		label: `${filterGroup} | ${value}`,
		page_type: 'listing',
		sub_page: 'overview',
		entity_id: entity?.entity_id?.toString() || null,
		entity_level: entity?.entity_level || null,
		value: {
			filter_group: filterGroup,
			value,
			geo,
		},
	});

export const onAccoClick = (
	position: number,
	acco: Search.CompactAccommodationItem,
	entity: Entity | null,
	isFlashDeal: boolean,
	offer?: Search.OfferItem | Search.AccommodationOnlyOffer,
	pageType: GTM.GTMPageType = 'listing',
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: pageType === 'listing' ? 'listing' : isFlashDeal ? 'flash_deals' : 'recommended_accomodations',
		molecule: 'acco_card',
		atom: 'link',
		action: 'click',
		label: null,
		page_type: pageType,
		sub_page: pageType === 'listing' ? 'accommodations' : null,
		entity_id: entity?.entity_id?.toString() || null,
		entity_level: entity?.entity_level || 'destination',
		value: {
			position,
			flash_deal: isFlashDeal,
			...accoEventValueConstructor(acco, undefined, offer, undefined, isFlashDeal),
		},
	});

export const onAccoSliderClick = (
	accoName: string,
	pageType: GTM.GTMPageType = 'listing',
	currentSlide: number,
	totalNumberOfSlides: number,
	slideDirection: 'right' | 'left',
	isFlashDeal: boolean,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'view',
		organism: null,
		molecule: 'acco_card',
		atom: 'photo_preview',
		action: 'view',
		label: accoName,
		page_type: pageType,
		sub_page: null,
		value: {
			current_slide: currentSlide,
			number_of_slides: totalNumberOfSlides,
			slide_direction: slideDirection,
			flash_deal: isFlashDeal,
		},
	});

export const onSearchBarInputOpen = (
	organism: 'site_navigation' | 'search_bar',
	molecule: 'quicksearch' | 'datepicker' | 'duration',
	pageType?: GTM.GTMPageType,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism,
		molecule,
		atom: null,
		action: 'open',
		label: null,
		page_type: pageType ? pageType : 'errorPage',
		sub_page: null,
		value: {},
	});

export const onQuickSearchSearch = (searchTerm: string, pageType?: GTM.GTMPageType): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'site_navigation',
		molecule: 'quicksearch',
		atom: null,
		action: 'search',
		label: null,
		page_type: pageType ? pageType : 'errorPage',
		sub_page: null,
		value: {
			search_term: searchTerm,
		},
	});

export const onSetPriceFilter = (
	label: 'priceMin' | 'priceMax',
	value: number | null,
	entity?: Entity | null,
): GTM.GTMEventV2 =>
	onFilterSelection(label, value?.toString() ?? '0', 'select', 'price_filter', 'value_input', entity);

export type BrandedSearchCardClickProps = { label: string; link: string; pageType: GTM.GTMPageType };
export const brandedSearchCardClick = ({ label, link, pageType }: BrandedSearchCardClickProps): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'click',
		organism: 'branded_search_cards',
		molecule: 'branded_search_card',
		atom: 'card',
		action: 'click',
		page_type: pageType,
		sub_page: null,
		label,
		value: {
			label,
			link,
		},
	});
